import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Navigate, useSearchParams } from 'react-router';

import {
  ANY_SPORT_PLAY_EVENTTYPE_ARENA_FIXTURE,
  ANY_SPORT_PLAY_EVENTTYPE_ARENA_FIXTURE_COMPETITION_COMPETITION_TRCKGRP,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_TRCKGRP,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import type { FixtureCompetitionPage_so5LeaderboardGroupInterface } from './__generated__/FixtureCompetitionPage.graphql.ts';

type Props = {
  arena?: boolean;
  so5LeaderboardGroupInterface: FixtureCompetitionPage_so5LeaderboardGroupInterface | null;
  fixture: string;
  eventType: string;
  competition: string;
};
export const FixtureCompetitionPage = ({
  arena,
  so5LeaderboardGroupInterface,
  fixture,
  eventType,
  competition,
}: Props) => {
  const { generateSportContextPath } = useSportContext();

  const [searchParams] = useSearchParams();
  const seasonality = searchParams.get('seasonality');

  const trackGroupSlugWithMostContendersAndLowerRarity = [
    ...(so5LeaderboardGroupInterface?.so5LeagueTrackGroups || []),
  ]
    .filter(
      (t: { so5LeagueTracks: Array<{ canCompose: { value: boolean } }> }) =>
        t.so5LeagueTracks.some(
          (so5LeagueTrack: { canCompose: { value: boolean } }) =>
            so5LeagueTrack.canCompose.value
        )
    )
    .sort(
      (
        a: {
          so5LeagueTracks: Array<{
            mySo5LeaderboardContenders: Array<{ slug: string }>;
          }>;
        },
        b: {
          so5LeagueTracks: Array<{
            mySo5LeaderboardContenders: Array<{ slug: string }>;
          }>;
        }
      ) => {
        const aContenders = a.so5LeagueTracks.reduce(
          (
            acc: number,
            value: { mySo5LeaderboardContenders: Array<{ slug: string }> }
          ) => acc + value.mySo5LeaderboardContenders.length,
          0
        );
        const bContenders = b.so5LeagueTracks.reduce(
          (
            acc: number,
            value: { mySo5LeaderboardContenders: Array<{ slug: string }> }
          ) => acc + value.mySo5LeaderboardContenders.length,
          0
        );

        return bContenders - aContenders;
      }
    )
    .shift()?.slug;

  if (!trackGroupSlugWithMostContendersAndLowerRarity) {
    return (
      <Navigate
        replace
        to={generateSportContextPath(
          arena
            ? ANY_SPORT_PLAY_EVENTTYPE_ARENA_FIXTURE
            : ANY_SPORT_PLAY_EVENTTYPE_FIXTURE,
          {
            params: { fixture, eventType },
          }
        )}
      />
    );
  }

  const params = seasonality ? `?seasonality=${seasonality}` : '';

  return (
    <Navigate
      replace
      to={
        generateSportContextPath(
          arena
            ? ANY_SPORT_PLAY_EVENTTYPE_ARENA_FIXTURE_COMPETITION_COMPETITION_TRCKGRP
            : ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_TRCKGRP,
          {
            params: {
              fixture,
              eventType,
              competition,
              trckGrp: trackGroupSlugWithMostContendersAndLowerRarity,
            },
          }
        ) + params
      }
    />
  );
};

FixtureCompetitionPage.fragments = {
  so5LeaderboardGroupInterface: gql`
    fragment FixtureCompetitionPage_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
      slug
      so5LeagueTrackGroups {
        slug
        so5LeagueTracks {
          slug
          mySo5LeaderboardContenders {
            slug
          }
          canCompose {
            value
          }
        }
      }
    }
  ` as TypedDocumentNode<FixtureCompetitionPage_so5LeaderboardGroupInterface>,
};
